/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import { Button, DropdownItemProps, Form } from "semantic-ui-react";
import { validators, createValidator } from "not-valid";
import * as moment from "moment";
import { User } from "reauthorize";

import { FormBaseComponent, FormState, SaveDispatchProps } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";

import { Link } from "@teamprevent/common/task/taskActions/models";
import {
    PRODUCT_TYPES,
    REFERRAL_FEATURES,
    CaseType,
    PHYSIOTHERAPY_ORIGINS,
    COVID_19_FEATURES,
    ASSESSMENT_AND_WELL_BEING_FEATURES,
    isYesNoOptionEnabledOptions,
    OhdPaymentMethodOptions,
    LinkObject
} from "@teamprevent/common/model";
import { AddAllowableEmailDomainModal } from "@teamprevent/common/customer/components/AddAllowableEmailDomainModal";
import { purchaseOrderNumberValidator } from "@teamprevent/common/ohCase/validators/purchaseOrderNumberValidator";
import { validLettersOnly } from "@teamprevent/common/validators/valid-letters-only";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { SNCRuleListModel } from "../../../sncRules/models";

import { ApplicationState } from "../../../applicationState";

import { customerCategoriesSelector } from "../../customerCategories/selectors";
import { CustomerCategory, loadCustomerCategories } from "../../customerCategories";

import { CustomerCreateModel, Customer, sectorOptions, invoicingMethodOptions, accessGroupOptions } from "../model";
import { basePathSelector } from "../selectors";
import { createCustomer } from "../actions";
import { selectors } from "../index";

import { GenericCaseType } from "../../../genericCaseTypes/models";
import { genericCaseTypesSelector } from "../../../genericCaseTypes/selectors";

import { loadGenericCaseTypes } from "../../../genericCaseTypes/actions";

import { loadSncRules } from "../../../sncRules/actions";

import { sncRulesSelector } from "../../../sncRules/selectors";

import { CreateModal } from "./CreateModal";
import { GenericCaseTypeEdit } from "./GenericCaseTypeEdit";

import { push } from "redux-little-router";

export interface CreateProps {
    open: boolean;
    close: () => void;
    customers: Customer[];
    customerCategories: CustomerCategory[];
    loadCustomerCategories: () => Promise<void>;
    loadGenericCaseTypes: () => Promise<void>;
    loadSncRules: () => Promise<void>;
    currentUser: User;
    genericCaseTypes: GenericCaseType[];
    sncRules: SNCRuleListModel[];
}

export interface DispatchProps extends SaveDispatchProps<CustomerCreateModel> {
}

interface CustomerCreateFormModel {
    id: string;
    name: string;
    customerCategory: Link | undefined;
    prefix: string;
    emailAddress: string;
    phoneNumber: string;
    customerNotes: string;
    active: boolean;
    products: CaseType[];
    physioOrigins: string[];
    referralFeatures: CaseType[];
    covid19Features: string[];
    assessmentAndWellBeingFeatures: string[];
    isMultiFactorAuthenticationEnabled: boolean;
    isEmployeeUserMultiFactorAuthenticationEnabled: boolean;
    isDefaultCustomerType: boolean;
    customerType: string;
    partners: Link[];
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    county: string;
    postCode: string;
    domainName: string;
    allowedEmailDomains: string[];
    keyContacts: string;
    accountCode: string;
    allowanceStartDate: moment.Moment;
    allowanceEndDate: moment.Moment;
    contractCode: string;
    invoicingMethod: "Automated" | "Manual" | null;
    priceBucket?: number;
    sectors: string[];
    financeCustomerName: string;
    orgLevel1Billing: boolean;
    ohdPaymentMethod: string;
    ohDirectCustomer: boolean;
    useToyotaTemplates: boolean;
    useOrgLevel1InMatchCriteria: boolean;
    purchaseOrderNumber: string;
    purchaseOrderRequiredPerCase: boolean;
    subscribed: boolean;
    accessGroupMode: string;
    ppqAutomationEnabled: boolean;
    hsNightWorkerAutomationEnabled: boolean;
    genericCaseTypes: LinkObject[];
    sncRule: LinkObject | undefined;
}

interface State extends FormState<CustomerCreateFormModel> {
    isAddAllowedEmailDomainModalOpen: boolean;
}

const CustomerCategoryOption_None = {
    text: "None",
    value: "none"
};

const PrefixValidationMessage = "Please use 3 to 6 characters, letters only";
const priceBucketValidator = createValidator<number>(value => value >= 1 && value <= 200, "Must be between 1 and 200");

export class Create extends FormBaseComponent<CustomerCreateFormModel, CreateProps, State> {
    private ppqAutomationFeatureEnabled: boolean = false;
    private hsCaseAutomationForNightWorker: boolean = false;
    private customerLevelPartialMatchChanges: boolean = false;
    private enableSNCAndFinanceInfoFunctionality: boolean = false;

    constructor(props: CreateProps & SaveDispatchProps<CustomerCreateModel>) {
        super(props);
        this.state = {
            values: {
                id: "0",
                name: "",
                customerCategory: undefined,
                prefix: "",
                emailAddress: "",
                phoneNumber: "",
                customerNotes: "",
                active: false,
                products: [],
                physioOrigins: [],
                referralFeatures: [],
                isMultiFactorAuthenticationEnabled: false,
                isEmployeeUserMultiFactorAuthenticationEnabled: false,
                covid19Features: [],
                assessmentAndWellBeingFeatures: [],
                isDefaultCustomerType: true,
                customerType: "",
                partners: [],
                allowedEmailDomains: [],
                addressLine1: "",
                addressLine2: "",
                addressLine3: "",
                city: "",
                county: "",
                postCode: "",
                domainName: "",
                keyContacts: "",
                accountCode: "",
                allowanceStartDate: null,
                allowanceEndDate: null,
                contractCode: "",
                invoicingMethod: null,
                sectors: [],
                financeCustomerName: "",
                orgLevel1Billing: false,
                ohdPaymentMethod: "",
                ohDirectCustomer: false,
                subscribed: false,
                useToyotaTemplates: false,
                useOrgLevel1InMatchCriteria: false,
                purchaseOrderNumber: "",
                purchaseOrderRequiredPerCase: false,
                accessGroupMode: "",
                ppqAutomationEnabled: false,
                hsNightWorkerAutomationEnabled: false,
                genericCaseTypes: [],
                sncRule: undefined
            },
            valid: {},
            isAddAllowedEmailDomainModalOpen: false
        };

        this.onAllowedEmailDomainAdd = this.onAllowedEmailDomainAdd.bind(this);
        this.onAllowedEmailDomainModalOpen = this.onAllowedEmailDomainModalOpen.bind(this);
        this.onAllowedEmailDomainClose = this.onAllowedEmailDomainClose.bind(this);
        this.onOhDirectCustomerChange = this.onOhDirectCustomerChange.bind(this);
        this.onSncRuleChange = this.onSncRuleChange.bind(this);

        const featureToggles = getFeatureToggles();
        this.ppqAutomationFeatureEnabled = featureToggles[FeatureToggleNames.EnablePPQAutomation];
        this.hsCaseAutomationForNightWorker = featureToggles[FeatureToggleNames.HSCaseAutomationForNightWorker];
        this.customerLevelPartialMatchChanges = featureToggles[FeatureToggleNames.CustomerLevelPartialMatchChanges];
        this.enableSNCAndFinanceInfoFunctionality = featureToggles[FeatureToggleNames.EnableSNCAndFinanceInfoFunctionality];
    }

    public override componentDidMount(): void {
        this.props.loadCustomerCategories();
        this.props.loadGenericCaseTypes();
        if (this.enableSNCAndFinanceInfoFunctionality) {
            this.props.loadSncRules();
        }
    }

    private onAllowedEmailDomainModalOpen() {
        this.setState({ ...this.state, isAddAllowedEmailDomainModalOpen: true });
    }

    private onAllowedEmailDomainClose() {
        this.setState({ ...this.state, isAddAllowedEmailDomainModalOpen: false });
    }

    private onAllowedEmailDomainAdd(newAllowedEmailDomain: string) {
        this.setState({
            ...this.state,
            values: {
                ...this.state.values,
                allowedEmailDomains: [...this.state.values.allowedEmailDomains, newAllowedEmailDomain]
            },
            isAddAllowedEmailDomainModalOpen: false
        });
    }

    private onSncRuleChange(value: string, valid: boolean ) {
        this.setState(prevState => ({
            ...prevState,
            values: {
                ...prevState.values,
                sncRule: this.props.sncRules.find(rule => rule.id === value)
            },
            valid: { ...prevState.valid, sncRule: valid }
        }));

    };

    private onOhDirectCustomerChange(value: boolean, valid: boolean) {
        if (value) {
            this.updateProperty("orgLevel1Billing", false, valid);
        }

        this.updateProperty("ohDirectCustomer", value, valid);
        this.updateProperty("ohdPaymentMethod", "", valid);
    }

    private onGenericCaseTypeDropDownChanged = (genericCaseTypesIds: LinkObject[]) => {
        this.setState({
            ...this.state,
            values: {
                ...this.state.values,
                genericCaseTypes: genericCaseTypesIds
            }
        });
    };

    public render() {
        const customerCategoryOptions: DropdownItemProps[] = [
            ...this.props.customerCategories.map(c => ({ text: c.name, value: c.id }))
        ];
        return (
            <>
                <Form onSubmit={this.handleSubmit}>
                    <h3>CUSTOMER INFORMATION</h3>
                    <Input.Checkbox
                        label="OH Direct Customer"
                        onChange={this.onOhDirectCustomerChange}
                        value={this.state.values.ohDirectCustomer}
                        showErrors={this.state.showErrors}
                    />
                    <Input.Dropdown
                        value={this.state.values.accessGroupMode}
                        label="Access Groups"
                        options={accessGroupOptions}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("accessGroupMode", value, valid)}
                        required
                    />
                    <Input.Checkbox
                        label="Subscribed"
                        onChange={(value, valid) => this.updateProperty("subscribed", value, valid)}
                        value={this.state.values.subscribed}
                        showErrors={this.state.showErrors}
                    />
                    <Input.Text
                        value={this.state.values.name}
                        label="Name *"
                        showErrors={this.state.showErrors}
                        required
                        onChange={(value, valid) => this.updateProperty("name", value, valid)}
                    />
                    <Input.Dropdown
                        value={this.state.values.customerCategory?.id ?? CustomerCategoryOption_None.value}
                        label="Customer Category *"
                        dynamicOptions
                        options={customerCategoryOptions}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateCustomerCategory(value, valid)}
                        required
                    />
                    <Input.Text
                        value={this.state.values.prefix}
                        label="Prefix *"
                        showErrors={this.state.showErrors}
                        required
                        validation={[
                            validators.validLength({ min: 3, max: 6, message: PrefixValidationMessage }),
                            validLettersOnly(PrefixValidationMessage)
                        ]}
                        onChange={(value, valid) => this.updateProperty("prefix", value, valid)}
                    />
                    <Input.Text
                        label="Address Line1"
                        value={this.state.values.addressLine1}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("addressLine1", value, valid)}
                    />
                    <Input.Text
                        label="Address Line2"
                        value={this.state.values.addressLine2}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("addressLine2", value, valid)}
                    />
                    <Input.Text
                        label="Address Line3"
                        value={this.state.values.addressLine3}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("addressLine3", value, valid)}
                    />
                    <Input.Text
                        label="City"
                        value={this.state.values.city}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("city", value, valid)}
                    />
                    <Input.Text
                        label="County"
                        value={this.state.values.county}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("county", value, valid)}
                    />
                    <Input.Text
                        label="Post Code *"
                        required
                        value={this.state.values.postCode}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("postCode", value, valid)}
                    />
                    <Input.Text
                        label="Domain Name"
                        value={this.state.values.domainName}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("domainName", value, valid)}
                    />
                    <>
                        <Input.DropdownMulti
                            label="Allowed Email Domains"
                            placeholder="Please add"
                            value={this.state.values.allowedEmailDomains}
                            showErrors={this.state.showErrors}
                            dynamicOptions
                            options={(this.state.values.allowedEmailDomains ?? []).map(aed => ({ text: aed, value: aed }))}
                            onChange={(value, valid) => this.updateProperty("allowedEmailDomains", value, valid)}
                        />
                        <Button secondary type="button" content="Add Allowed Email Domain" onClick={this.onAllowedEmailDomainModalOpen} />
                    </>
                    <Input.Text
                        label="Key Contacts"
                        value={this.state.values.keyContacts}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("keyContacts", value, valid)}
                    />
                    <Input.DropdownMulti
                        label="Sectors"
                        placeholder="Please select"
                        value={this.state.values.sectors}
                        showErrors={this.state.showErrors}
                        dynamicOptions
                        options={sectorOptions}
                        onChange={(value, valid) => this.updateProperty("sectors", value, valid)}
                    />
                    <Input.Email
                        value={this.state.values.emailAddress}
                        label="Email Address"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("emailAddress", value, valid)}
                    />
                    <Input.Text
                        value={this.state.values.phoneNumber}
                        label="Phone Number"
                        showErrors={this.state.showErrors}
                        validation={[validators.validPhoneNumber(10)]}
                        onChange={(value, valid) => this.updateProperty("phoneNumber", value, valid)}
                    />
                    <Input.DropdownMulti
                        options={PRODUCT_TYPES
                            .filter(p => !REFERRAL_FEATURES.some(f => f.value === p.value))
                            .map(c => ({ text: c.displayName, value: c.value }))}
                        value={this.state.values.products}
                        label="Products"
                        showErrors={this.state.showErrors}
                        onChange={this.updateProducts}
                    />
                    {
                        this.hsCaseAutomationForNightWorker && this.state.values.products?.includes(CaseType.HEALTH_SURVEILLANCE) &&
                        <Input.Dropdown
                            value={this.state.values.hsNightWorkerAutomationEnabled?.toString()}
                            label="Automated HS"
                            options={isYesNoOptionEnabledOptions.map(c => ({ text: c.text, value: c.value }))}
                            showErrors={this.state.showErrors}
                            onChange={(value, valid) => this.updateProperty("hsNightWorkerAutomationEnabled", value, valid)}
                        />
                    }
                    {
                        this.state.values.products?.includes(CaseType.GENERIC) &&
                        <GenericCaseTypeEdit
                            allGenericCaseTypes={this.props.genericCaseTypes}
                            selectedGenericCaseTypes={this.state.values.genericCaseTypes}
                            onGenericCaseTypeChange={this.onGenericCaseTypeDropDownChanged}
                        />
                    }
                    {
                        this.state.values.products?.includes(CaseType.PHYSIOTHERAPY_REFERRAL) &&
                    <Input.DropdownMulti
                        options={PHYSIOTHERAPY_ORIGINS.map(c => ({ text: c.displayName, value: c.value }))}
                        value={this.state.values.physioOrigins}
                        label="Physiotherapy Origins"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("physioOrigins", value, valid)}
                    />
                    }
                    {
                        this.state.values.products?.includes(CaseType.REFERRAL) &&
                    <Input.DropdownMulti
                        options={REFERRAL_FEATURES.map(c => ({ text: c.displayName, value: c.value }))}
                        value={this.state.values.referralFeatures}
                        label="Referral Features"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("referralFeatures", value, valid)}
                    />
                    }
                    {
                        this.state.values.products?.includes(CaseType.COVID_19) &&
                    <Input.DropdownMulti
                        options={COVID_19_FEATURES.map(c => ({ text: c.displayName, value: c.value }))}
                        value={this.state.values.covid19Features}
                        label="COVID-19 Features"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("covid19Features", value, valid)}
                    />
                    }
                    {this.state.values.products?.includes(CaseType.SRA) &&
                    <Input.DropdownMulti
                        options={ASSESSMENT_AND_WELL_BEING_FEATURES.map(c => ({ text: c.displayName, value: c.value }))}
                        value={this.state.values.assessmentAndWellBeingFeatures}
                        label="Assessment and Well-being"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("assessmentAndWellBeingFeatures", value, valid)}
                    />
                    }
                    {
                        this.ppqAutomationFeatureEnabled && this.state.values.products?.includes(CaseType.PPQ) &&
                        <Input.Dropdown
                            value={this.state.values.ppqAutomationEnabled?.toString()}
                            label="Automated PPQ"
                            options={isYesNoOptionEnabledOptions.map(c => ({ text: c.text, value: c.value }))}
                            showErrors={this.state.showErrors}
                            onChange={(value, valid) => this.updateProperty("ppqAutomationEnabled", value, valid)}
                        />
                    }
                    <Input.DropdownMulti
                        dynamicOptions
                        label="Partners"
                        value={this.state.values.partners.map(p => p.id)}
                        showErrors={this.state.showErrors}
                        options={this.getCustomerOptions()}
                        onChange={this.updatePartners}
                        search
                    />
                    <Input.Text
                        value={this.state.values.customerNotes}
                        label="Customer Notes"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("customerNotes", value, valid)}
                    />
                    <Input.Dropdown
                        value={this.state.values.isDefaultCustomerType.toString()}
                        label="Default Customer Type"
                        options={isYesNoOptionEnabledOptions.map(c => ({ text: c.text, value: c.value }))}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("isDefaultCustomerType", value === "true", valid)}
                    />
                    {this.state.values.isDefaultCustomerType === false &&
                        <Input.Text
                            value={this.state.values.customerType}
                            label="Customer Type"
                            showErrors={this.state.showErrors}
                            onChange={(value, valid) => this.updateProperty("customerType", value, valid)}
                        />
                    }
                    <Input.Checkbox
                        label="Use Toyota Templates"
                        onChange={(value, valid) => this.updateProperty("useToyotaTemplates", value, valid)}
                        value={this.state.values.useToyotaTemplates}
                        showErrors={this.state.showErrors}
                    />
                    {this.customerLevelPartialMatchChanges && <Input.Checkbox
                        label="Use Org Level 1 in Match Criteria"
                        // eslint-disable-next-line react/jsx-no-bind
                        onChange={(value, valid) => this.updateProperty("useOrgLevel1InMatchCriteria", value, valid)}
                        value={this.state.values.useOrgLevel1InMatchCriteria}
                        showErrors={this.state.showErrors}
                    />}

                    <h3>MULTI FACTOR AUTHENTICATION (MFA)</h3>
                    <Input.Dropdown
                        value={this.state.values.isMultiFactorAuthenticationEnabled?.toString()}
                        label="Customer Users"
                        options={isYesNoOptionEnabledOptions.map(c => ({ text: c.text, value: c.value }))}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("isMultiFactorAuthenticationEnabled", value, valid)}
                    />
                    <Input.Dropdown
                        value={this.state.values.isEmployeeUserMultiFactorAuthenticationEnabled?.toString()}
                        label="Employees"
                        options={isYesNoOptionEnabledOptions.map(c => ({ text: c.text, value: c.value }))}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("isEmployeeUserMultiFactorAuthenticationEnabled", value, valid)}
                    />

                    <h3>FINANCE INFORMATION</h3>
                    {this.state.values.ohDirectCustomer &&
                    <Input.Dropdown
                        label="OHD Payment Method"
                        value={this.state.values.ohdPaymentMethod}
                        options={OhdPaymentMethodOptions.map(t => ({ text: t, value: t }))}
                        required
                        onChange={(value, valid) => this.updateProperty("ohdPaymentMethod", value, valid)}
                    />
                    }
                    <Input.Text
                        value={this.state.values.financeCustomerName}
                        label="Finance Customer Name"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("financeCustomerName", value, valid)}
                    />
                    <Input.Text
                        value={this.state.values.accountCode}
                        label="Finance Customer Reference"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("accountCode", value, valid)}
                    />
                    <Input.Text
                        value={this.state.values.contractCode}
                        label="Finance Cost Centre"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("contractCode", value, valid)}
                    />
                    <Input.Dropdown
                        value={this.state.values.invoicingMethod}
                        label="Invoicing Method"
                        options={invoicingMethodOptions}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("invoicingMethod", value, valid)}
                        required
                    />
                    {
                        this.state.values.invoicingMethod === "Automated" &&
                        <Input.Number
                            value={this.state.values.priceBucket}
                            label="Price Bucket"
                            showErrors={this.state.showErrors}
                            onChange={(value, valid) => this.updateProperty("priceBucket", value, valid)}
                            required
                            validation={[priceBucketValidator]}
                        />
                    }

                    {this.enableSNCAndFinanceInfoFunctionality &&
                        <Input.Dropdown
                            value={this.state.values.sncRule?.id}
                            dynamicOptions
                            label="SNC Rules"
                            options={this.getSncRuleOptions()}
                            showErrors={this.state.showErrors}
                            onChange={this.onSncRuleChange}
                            required
                        />
                    }

                    <Input.Text
                        value={this.state.values.purchaseOrderNumber}
                        label="Customer Purchase Order Number"
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("purchaseOrderNumber", value, valid)}
                        validation={[purchaseOrderNumberValidator]}
                    />
                    <Input.Date
                        label={"Allowance Start Date"}
                        value={this.state.values.allowanceStartDate}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("allowanceStartDate", value, valid)}
                    />
                    <Input.Date
                        label={"Allowance End Date"}
                        value={this.state.values.allowanceEndDate}
                        showErrors={this.state.showErrors}
                        onChange={(value, valid) => this.updateProperty("allowanceEndDate", value, valid)}
                    />
                    {!this.state.values.ohDirectCustomer &&
                    <Input.Checkbox
                        label="Org level 1 billing"
                        onChange={(value, valid) => this.updateProperty("orgLevel1Billing", value, valid)}
                        value={this.state.values.orgLevel1Billing}
                        showErrors={this.state.showErrors}
                    />
                    }
                    <Input.Checkbox
                        label="Purchase Order required per Case"
                        onChange={(value, valid) => this.updateProperty("purchaseOrderRequiredPerCase", value, valid)}
                        value={this.state.values.purchaseOrderRequiredPerCase}
                        showErrors={this.state.showErrors}
                    />
                </Form>
                <AddAllowableEmailDomainModal
                    isAddAllowedEmailDomainModalOpen={this.state.isAddAllowedEmailDomainModalOpen}
                    onAllowedEmailDomainAdd={this.onAllowedEmailDomainAdd}
                    onAllowedEmailDomainClose={this.onAllowedEmailDomainClose}
                />
            </>
        );
    }

    private getCustomerOptions = () => {
        return [
            ...this.props.customers.map(customer => ({ key: customer.id, value: customer.id, text: customer.name }))
        ];
    };

    private getSncRuleOptions = () => {
        if (!this.props.sncRules) {
            return [];
        }

        return [
            ...this.props.sncRules.map(rule => ({ key: rule.id, value: rule.id, text: rule.name }))
        ];
    };

    private updateProducts = (value: CaseType[], valid: boolean) => {
        this.setState(prevState => ({
            values: {
                ...prevState.values,
                physioOrigins: value.includes(CaseType.PHYSIOTHERAPY_REFERRAL) ? prevState.values.physioOrigins : [],
                referralFeatures: value.includes(CaseType.REFERRAL) ? prevState.values.referralFeatures : [],
                products: value,
                covid19Features: value.includes(CaseType.COVID_19) ? prevState.values.covid19Features : [],
                assessmentAndWellBeingFeatures: value.includes(CaseType.SRA) ? prevState.values.assessmentAndWellBeingFeatures : []
            },
            valid: { ...prevState.valid, products: valid }
        }) );
    };

    private updatePartners = (value: string[], valid: boolean) => {

        const links: Link[] = value.map(v => {
            return { id: v, name: this.props.customers.find(c => c.id === v)?.name };
        });

        this.setState(prevState => ({
            values: {
                ...prevState.values,
                partners: links
            },
            valid: { ...prevState.valid, partners: valid }
        }) );
    };

    private updateCustomerCategory = (value: string, valid: boolean) => {
        const category: CustomerCategory | undefined = value === CustomerCategoryOption_None.value
            ? undefined
            : this.props.customerCategories.find(cc => cc.id === value);

        this.setState(prevState => ({
            values: {
                ...prevState.values,
                customerCategory: category === undefined ? null : {
                    id: category.id,
                    name: category.name
                }
            },
            valid: { ...prevState.valid, customerCategory: valid }
        }) );
    };

    public submit(): void {
        this.handleSubmit({ preventDefault: (): void => undefined } as any);
    }
}

function mapStateToProps(state: ApplicationState) {
    return {
        open: state.router.pathname?.endsWith("/create"),
        basePath: basePathSelector(state),
        customers: selectors.customersSelector(state),
        customerCategories: customerCategoriesSelector(state),
        currentUser: state.currentUser,
        genericCaseTypes: genericCaseTypesSelector(state),
        sncRules: sncRulesSelector(state),
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, undefined, any>) {
    return {
        dispatchSave: (customer: CustomerCreateFormModel, basePath: string) => {
            const { referralFeatures, ...model } = customer;
            dispatch(createCustomer({ ...model, products: [...model.products, ...referralFeatures] }, basePath));
        },
        dispatchClose: (basePath: string) => dispatch(push(basePath, {})),
        dispatchLoadCustomerCategories: () => dispatch(loadCustomerCategories()),
        dispatchLoadGenericCaseTypes: () => dispatch(loadGenericCaseTypes()),
        dispatchLoadSncRules: () => dispatch(loadSncRules()),
    };
}

function mergeProps(propsFromState: any, propsFromDispatch: any): CreateProps & DispatchProps {
    return {
        open: propsFromState.open,
        customers: propsFromState.customers,
        customerCategories: propsFromState.customerCategories,
        save: (customer: CustomerCreateModel) => propsFromDispatch.dispatchSave(customer, propsFromState.basePath),
        close: () => propsFromDispatch.dispatchClose(propsFromState.basePath),
        loadCustomerCategories: propsFromDispatch.dispatchLoadCustomerCategories,
        loadGenericCaseTypes: propsFromDispatch.dispatchLoadGenericCaseTypes,
        loadSncRules: propsFromDispatch.dispatchLoadSncRules,
        currentUser: propsFromState.currentUser,
        genericCaseTypes: propsFromState.genericCaseTypes,
        sncRules: propsFromState.sncRules,
    };
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(CreateModal);
